import { useApi } from '@shared/hooks/useApi';
import { User, UserSettings } from '@shared/protos/user';
import { createContext, useContext, useEffect, useState, useCallback } from 'react';

export const UserContext = createContext<User | undefined>(undefined);
UserContext.displayName = 'UserContext';

export const UserSettingsContext = createContext<
  { user: User; userSettings: UserSettings; updateUserSettings: (newSettings: UserSettings) => Promise<void> } | undefined
>(undefined);
UserSettingsContext.displayName = 'UserSettingsContext';

export default function UserContextProvider(props: any) {
  const { apiClient } = useApi();
  const [user, setUser] = useState<User>();
  const [userSettings, setUserSettings] = useState<UserSettings>();

  const fetchUserSettings = useCallback(async () => {
    if (apiClient) {
      const newUserSettings = await apiClient.getUserSettings();
      setUserSettings(newUserSettings);
    }
  }, [apiClient]);

  const updateUserSettings = useCallback(
    async (newSettings: UserSettings) => {
      const mergedSettings = {
        ...userSettings,
        ...newSettings,
        dashboard: {
          ...userSettings?.dashboard,
          ...newSettings.dashboard,
        },
      };

      setUserSettings(mergedSettings);

      try {
        await apiClient?.updateUserSettings(mergedSettings);
      } catch (error) {
        await fetchUserSettings();
        throw error;
      }
    },
    [apiClient, fetchUserSettings, userSettings]
  );

  useEffect(() => {
    async function fetchUser() {
      const newUser = await apiClient?.getUser();
      setUser(newUser);
    }

    fetchUser();
    fetchUserSettings();
  }, [apiClient, fetchUserSettings]);

  return (
    <UserContext.Provider value={user} {...props}>
      <UserSettingsContext.Provider value={user && userSettings ? { user, userSettings, updateUserSettings } : undefined}>
        {props.children}
      </UserSettingsContext.Provider>
    </UserContext.Provider>
  );
}

export function useUserContext(): User | undefined {
  return useContext(UserContext);
}

export function useUserSettingsContext():
  | { user: User; userSettings: UserSettings; updateUserSettings: (newSettings: UserSettings) => Promise<void> }
  | undefined {
  return useContext(UserSettingsContext);
}

export function useUserSettings(): UserSettings | undefined {
  const context = useContext(UserSettingsContext);
  return context?.userSettings;
}

export function useUpdateUserSettings() {
  return useContext(UserSettingsContext)?.updateUserSettings;
}
